import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, Phone } from 'lucide-react';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setIsLangMenuOpen(false);
  };

  const navItems = [
    { key: 'home', href: '#home' },
    { key: 'about', href: '#about' },
    { key: 'services', href: '#services' },
    { key: 'projects', href: '#projects' },
    { key: 'contact', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-strong py-3' : 'py-4 md:py-5 bg-white/60 backdrop-blur-sm'
      }`}
      style={{ WebkitBackfaceVisibility: 'hidden', backfaceVisibility: 'hidden' }}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.a
            href="#home"
            onClick={(e) => { e.preventDefault(); scrollToSection('#home'); }}
            className="flex items-center gap-3 group"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="logo"
          >
            <div className="w-9 h-9 rounded-lg bg-zinc-950 flex items-center justify-center">
              <span className="font-outfit font-bold text-white text-lg">M</span>
            </div>
            <span className="font-outfit font-semibold text-lg hidden sm:block text-zinc-950">
              Med Stud <span className="text-sky-500">Tech</span>
            </span>
          </motion.a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={(e) => { e.preventDefault(); scrollToSection(item.href); }}
                className="font-manrope text-sm text-zinc-600 hover:text-zinc-950 transition-colors relative group"
                data-testid={`nav-${item.key}`}
              >
                {t(`nav.${item.key}`)}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-zinc-950 group-hover:w-full transition-all duration-300" />
              </a>
            ))}
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-3">
            {/* Phone Button - épuré */}
            <a
              href="tel:+33605550522"
              className="hidden md:flex items-center gap-2 px-4 py-2 rounded-md border border-zinc-200 text-zinc-700 hover:bg-zinc-50 transition-colors text-sm"
              data-testid="phone-btn"
            >
              <Phone className="w-4 h-4 text-emerald-500" />
              <span className="hidden lg:inline">+33 6 05 55 05 22</span>
            </a>

            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-200 hover:bg-zinc-50 transition-colors"
                data-testid="language-switcher"
              >
                <Globe className="w-4 h-4 text-zinc-500" />
                <span className="text-sm font-medium uppercase text-zinc-700">{i18n.language}</span>
              </button>

              <AnimatePresence>
                {isLangMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    className="absolute top-full right-0 mt-2 bg-white border border-zinc-200 rounded-md overflow-hidden min-w-[110px] shadow-sm"
                  >
                    <button
                      onClick={() => toggleLanguage('fr')}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-zinc-50 transition-colors ${
                        i18n.language === 'fr' ? 'text-zinc-950 font-medium' : 'text-zinc-500'
                      }`}
                      data-testid="lang-fr"
                    >
                      Français
                    </button>
                    <button
                      onClick={() => toggleLanguage('en')}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-zinc-50 transition-colors ${
                        i18n.language === 'en' ? 'text-zinc-950 font-medium' : 'text-zinc-500'
                      }`}
                      data-testid="lang-en"
                    >
                      English
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* CTA Button (Desktop) - épuré */}
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); scrollToSection('#contact'); }}
              className="hidden md:flex items-center gap-2 px-5 py-2.5 rounded-md bg-zinc-950 text-white font-medium text-sm hover:bg-zinc-800 transition-colors btn-press"
              data-testid="nav-cta"
            >
              {t('hero.cta')}
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-md border border-zinc-200 text-zinc-700"
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden mt-4 bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm"
            >
              <div className="p-4 flex flex-col gap-1">
                {navItems.map((item, index) => (
                  <motion.a
                    key={item.key}
                    href={item.href}
                    onClick={(e) => { e.preventDefault(); scrollToSection(item.href); }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="py-3 px-4 rounded-md hover:bg-zinc-50 text-zinc-700 hover:text-zinc-950 transition-colors"
                    data-testid={`mobile-nav-${item.key}`}
                  >
                    {t(`nav.${item.key}`)}
                  </motion.a>
                ))}
                <a
                  href="#contact"
                  onClick={(e) => { e.preventDefault(); scrollToSection('#contact'); }}
                  className="mt-2 py-3 px-4 rounded-md bg-zinc-950 text-white font-medium text-center"
                  data-testid="mobile-nav-cta"
                >
                  {t('hero.cta')}
                </a>
                <a
                  href="tel:+33605550522"
                  className="mt-1 py-3 px-4 rounded-md border border-zinc-200 text-zinc-700 font-medium text-center flex items-center justify-center gap-2"
                  data-testid="mobile-phone-cta"
                >
                  <Phone className="w-4 h-4 text-emerald-500" />
                  +33 6 05 55 05 22
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
}
